import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Trans } from 'react-i18next';

import Form from '../../../../shared/components/Form';
import {View} from '../../../../shared/components';
import AgreeTerms from '../../../../shared/components/AgreeTerms';
import Text from '../../../../shared/components/Text';

import { navigation } from '../../../../shared/constants';
import PRICES from '../../../../shared/prices';
import { styleUtils } from '../../../../shared/utils';
import { COLORS } from '../../../../shared/design/tokens';

import styles from './styles';
import {ROUTES} from "../../../../shared/constants/navigation";
import FORMAT_PATTERNS from "../../../../shared/constants/format";
import { submitOrder } from "../../../../services/onboarding-api"
import {mapToFormSubmissionEntity} from "../../../../shared/mappers/onboardingMapper";
import {default as OrderMessage} from "../../../../shared/components/Message";

const { style } = styleUtils;
const {
  EXTERNAL: { termsAndConditions }
} = navigation;

const Container = style(View, styles.container);
const InvoiceContainer = style(View, styles.invoiceContainer);
const InvoiceInfoContainer = style(View, styles.invoiceInfoContainer);
const BillingContainer = style(View, styles.billingContainer);
const BillingSection = style(View, styles.billingSection);
const Value = style(Text, styles.value);

const OrderDetailsTitle = style(Text, styles.orderDetailsTitle);
const BillingTitle = style(Text, styles.billingTitle);

const SectionContainer = style(View, styles.sectionContainer);
const TitleContainer = style(View, styles.titleContainer);
const Title = style(Text, styles.title);
const DescriptionCointainer = style(View, styles.descriptionCointainer);
const Description = style(Text, styles.description);
const RowContainer = style(View, styles.rowContainer);
const Label = style(Text, styles.label);
const PriceContainer = style(View, styles.priceContainer);
const Price = style(Text, styles.price);
const Per = style(Text, styles.per);

const BillingLabel = style(Text, styles.billingLabel);

const TotalContainer = style(View, styles.totalContainer);
const TotalValueContainer = style(View, styles.totalValueContainer);
const TotalLabel = style(Text, styles.totalLabel);
const TotalValue = style(Text, styles.totalValue);
const TotalValuePer = style(Text);

const AgreeTermsLink = style('a', styles.agreeTermsLink);
const MessageContainer = style(View, styles.messageContainer);

const Invoice = ({ id, t, data, billing, showtitle }) => (
  <InvoiceContainer>
    <InvoiceInfo id={id} t={t} data={data} showtitle={showtitle} />
    <Billing t={t} billing={billing} {...billing} showtitle={showtitle} />
  </InvoiceContainer>
);

const Billing = ({
  t,
  billing,
  name,
  email,
  billingAddress,
  billingFrequency,
  startDate,
  showtitle
}) => {
  return (
    <BillingContainer>
      {name && (
        <>
          {showtitle && (
            <BillingTitle size="xlarge">{t('billingTitle')}</BillingTitle>
          )}
          <BillingSection>
            <BillingLabel nopadding={!showtitle} weight="900">
              {t('address')}
            </BillingLabel>
            <Value>{billingAddress}</Value>
            <BillingLabel weight="900">{t('contact')}</BillingLabel>
            <Value>{name}</Value>
            <Value size="placeholder">{email}</Value>
            <BillingLabel weight="900">{t('frequency.label')}</BillingLabel>
            <Value>{billingFrequency}</Value>
            { startDate &&
              <>
                <BillingLabel weight="900">{t('startDate')}</BillingLabel>
                <Value>{startDate}</Value>
              </>
            }
          </BillingSection>
        </>
      )}
    </BillingContainer>
  );
};

const InvoiceInfo = ({
  id,
  t,
  data: { sections, total, totalPer },
  showtitle
}) => (
  <InvoiceInfoContainer>
    {showtitle && (
      <OrderDetailsTitle size="xlarge">
        {t('orderDetailsTitle')}
      </OrderDetailsTitle>
    )}
    {sections.map((section, index) => (
      <SectionInfo key={index} {...section} />
    ))}

    <TotalContainer>
      <TotalLabel size="large" color={COLORS.TEXT.SEA_BLUE} weight="900">
        {t('total')}
      </TotalLabel>
      <TotalValueContainer>
        <TotalValue size="large" color={COLORS.TEXT.SEA_BLUE} weight="900" format={FORMAT_PATTERNS.currency}>
          {total}
        </TotalValue>
        <TotalValuePer size="placeholder">{totalPer}</TotalValuePer>
      </TotalValueContainer>
    </TotalContainer>
  </InvoiceInfoContainer>
);

const SectionInfo = ({ title, description, values }) => (
  <SectionContainer>
    <TitleContainer>
      <Title weight="900">{title}</Title>
      <DescriptionCointainer>
        <Description>{description}</Description>
      </DescriptionCointainer>
    </TitleContainer>
    {values.map(({ label, price, per }) => (
      <RowContainer key={`${description}_${label}`}>
        <Label>{label}</Label>
        <PriceContainer>
          <Price>{price}</Price>
          {per && <Per size="placeholder">{per}</Per>}
        </PriceContainer>
      </RowContainer>
    ))}
  </SectionContainer>
);

class OrderDetails extends Component {
  state = {
    agreeTermsChecked: false,
    error: false,
    orderErrorMsg: null
  };

  mapDescriptions = (instancesDescription = []) => {
    const descriptions = {};
    instancesDescription.map(
      ({ instanceId, description }) => (descriptions[instanceId] = description)
    );
    return descriptions;
  };

  getBillingData = (t, billing, instancesSetup) => {
    const {
      name,
      email,
      billingAddress,
      billingFrequency
    } = billing;

    const { instanceLaunchDate } = instancesSetup;

    return {
      name,
      email,
      billingAddress,
      billingFrequency: `${t(`frequency.options.${billingFrequency}`)} ${t(
        `billing.options.usd`
      )}`,
      startDate: instanceLaunchDate ? moment(instanceLaunchDate).format('DD MMMM YYYY') : null
    };
  };

  separateInvoiceData = (
    t,
    billing,
    instances,
    addOns,
    instancesDescription
  ) => {
    this.date = [];
    let launchDates = [];
    const { total = 0 } = this.props.formData;
    let invoice = { sections: [], total };

    Object.keys(instances)
      .filter(instanceId => instanceId !== 'instancesSetup')
      .forEach(id => {
        const instance = instances[id];
        const instanceData = {
          title: `Instance #${id}`,
          description: instancesDescription[id].replace(
            `Instance #${id} - `,
            ''
          ),
          values: [
            {
              label: t('setupFee'),
              price: `$${PRICES.setupFee[instance.hostingPlan]}`,
              per: ` / ${t('once')}`
            },
            {
              label: t('hosting'),
              price: `$${PRICES.hostingPlan[instance.hostingPlan]}`,
              per: ` / ${t('month')}`
            }
          ]
        };

        invoice = {
          ...invoice,
          sections: [...invoice.sections, instanceData]
        };

        launchDates = [
          ...launchDates,
          (instance.instanceLaunchDate || moment()).format('DD MMMM YYYY')
        ];
      });

    this.date = launchDates.length
      ? launchDates.sort()[0]
      : moment().format('DD MMMM YYYY');

    const addOnsTitle = {
      title: t('addOnsTitle'),
      values: []
    };

    const mainInvoiceAddOnValues = [];

    const domainAddOn = {
      label: t('domain'),
      price: `$${addOns && addOns.domain ? addOns.domain : 0}`,
      per: ` / ${t('year')}`
    };

    const integrationDriver = {
      label: t('integrationDriver'),
      price: `$${addOns && addOns.integrationDriver ? PRICES.integrationDriver : 0}`,
      per: ` / ${t('month')}`
    };

    const dhis2Support = {
      label: t('dhis2Support'),
      price: '$0'
    };

    addOns && addOns.domain && mainInvoiceAddOnValues.push(domainAddOn);
    addOns && addOns.integrationDriver && mainInvoiceAddOnValues.push(integrationDriver);
    addOns && addOns.dhis2SupportPlan && mainInvoiceAddOnValues.push(dhis2Support);

    invoice = {
      ...invoice,
      sections: [
        ...invoice.sections,
        mainInvoiceAddOnValues.length > 0 ? { ...addOnsTitle, values: mainInvoiceAddOnValues } : null
      ].filter(Boolean)
    };

    const servicePeriod =
      instances &&
      instances.instancesSetup &&
      instances.instancesSetup.servicePeriod
        ? instances.instancesSetup.servicePeriod
        : 12;

    return {
      ...invoice,
      totalPer: t('totalPer').replace('{0}', servicePeriod)
    };
  };

  handleFormSubmit = () => {
    const { history, formData } = this.props;
    const { agreeTermsChecked } = this.state;
    if (agreeTermsChecked) {
      const orderPayload = mapToFormSubmissionEntity(formData);
      submitOrder(orderPayload).then((res, a) => {
        if(!res.message) {
          history.push(ROUTES.SIGN_UP_CONFIRMATION)
        } else {
          this.setState({orderErrorMsg: res && res.message})
        }
      })
        .catch(({response}) => {
          const {status, data: {message}} = response;
          if (status < 500) {
            this.setState({orderErrorMsg: message})
          } else {
            this.setState({orderErrorMsg: this.props.t('submit.genericError')})
          }
        });
    } else {
      this.setState({ error: true });
    }
  };

  handleOnAgreeTermsChange = agreeTermsChecked => {
    this.setState({ agreeTermsChecked, error: !agreeTermsChecked });
  };

  render() {
    const {
      t,
      formData: {
        instances: { instances } = {},
        addOns,
        billing
      },
      history,
      instancesDescription
    } = this.props;

    if(!instancesDescription) {
      history.push(ROUTES.SIGN_UP_INSTANCES);
      return false
    }

    const { error } = this.state;
    const invoice = this.separateInvoiceData(
      t,
      billing,
      instances,
      addOns,
      this.mapDescriptions(instancesDescription)
    );
    const billingData = this.getBillingData(t, billing, instances.instancesSetup);
    const errorMessage = this.state.orderErrorMsg !== null && (
      <MessageContainer>
        <OrderMessage type="error">
          <Text color={COLORS.MESSAGE.ERROR.TEXT} weight={600} size="small">{this.state.orderErrorMsg}</Text>
        </OrderMessage>
      </MessageContainer>
    );

    return (
      <>
        <Container>
          <Invoice t={t} data={invoice} billing={billingData} showtitle />
          <AgreeTermsConfirm
            t={t}
            onChange={this.handleOnAgreeTermsChange}
            error={error}
          />
          {errorMessage}
          <Form
            fluid
            showOnlyButtons
            title={t('title')}
            previousButton={{
              label: t('previousButtonLabel'),
              action: () => history.push(ROUTES.SIGN_UP_BILLING)
            }}
            nextButton={{
              label: t('nextButtonLabel'),
              action: this.handleFormSubmit
            }}
          />
        </Container>
      </>
    );
  }
}

const AgreeTermsConfirm = ({ t, onChange, error }) => {
  const message = error ? t('agreeTermsRequired') : null;
  return (
    <AgreeTerms onChange={onChange} error={error} message={message}>
      <Trans
        t={t}
        i18nKey="agreeTermsLink"
        components={[
          <AgreeTermsLink target="_blank" href={termsAndConditions} />
        ]}
      />
    </AgreeTerms>
  );
};

OrderDetails.propTypes = {
  handlePreviousStepClick: PropTypes.func,
  handleNextStepClick: PropTypes.func,
  instancesDescription: PropTypes.array,
  formData: PropTypes.object
};

export default OrderDetails;
